import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeft } from 'lucide-react';

/**
 * StandardPageHeader Component
 * Provides consistent header structure across pages: [Back] [Title] [Actions]
 *
 * @param {string} title - Page title to display
 * @param {string} backTo - Optional back navigation path (e.g., "/setlists")
 * @param {string} backLabel - Optional back button label (defaults to "Back")
 * @param {Function} onBackClick - Optional custom back handler (overrides backTo)
 * @param {ReactNode} actions - Optional action buttons (right-aligned)
 * @param {string} className - Optional additional CSS classes
 */
const StandardPageHeader = ({
  title,
  backTo,
  backLabel = 'Back',
  onBackClick,
  actions,
  className = ''
}) => {
  const navigate = useNavigate();

  const handleBack = () => {
    if (onBackClick) {
      onBackClick();
    } else if (backTo) {
      navigate(backTo);
    } else {
      navigate(-1); // Default to browser back
    }
  };

  // Only show back button if backTo is provided or onBackClick is defined
  const showBackButton = backTo || onBackClick;

  return (
    <div className={`px-4 py-3 border-b border-border bg-background ${className}`}>
      <div className="flex items-center justify-between gap-4">
        {/* Left side: Back button + Title */}
        <div className="flex items-center gap-2 min-w-0 flex-1">
          {showBackButton && (
            <button
              onClick={handleBack}
              className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
              aria-label={backLabel}
            >
              <ChevronLeft className="w-4 h-4" />
              <span>{backLabel}</span>
            </button>
          )}

          <h1 className="text-xl font-bold truncate">
            {title}
          </h1>
        </div>

        {/* Right side: Action buttons */}
        {actions && (
          <div className="flex items-center gap-2 flex-shrink-0">
            {actions}
          </div>
        )}
      </div>
    </div>
  );
};

export default StandardPageHeader;
